import { useState } from "react";
import { adminApi, imgOf } from "../lib";
import { Panel, In, Uploader, Thumbs, useAdmin, save, Check, SampleBadge } from "./parts";

export function HomeTab() {
  const [s, load, setS] = useAdmin("/settings");
  const [arts] = useAdmin("/c/artworks");
  if (!s) return null;
  const set = (k) => (v) => setS({ ...s, [k]: v });
  const toggleArt = (id, on) => setS({ ...s, featured_artwork_ids: on ? [...s.featured_artwork_ids, id] : s.featured_artwork_ids.filter((x) => x !== id) });
  const submit = async () => { await save(() => adminApi.put("/settings", s)); load(); };
  return (
    <>
      <Panel title="Homepage" testid="admin-home-panel"
        actions={<button className="lou-btn !py-2 text-sm" onClick={submit} data-testid="admin-settings-save">Save homepage</button>}>
        <div className="grid md:grid-cols-2 gap-5">
          <In label="Name" value={s.name} onChange={set("name")} testid="admin-name" />
          <In label="Life dates (optional, as the family wishes them shown)" value={s.life_dates} onChange={set("life_dates")} testid="admin-life-dates" />
        </div>
        <div className="mt-5"><In label="Introduction" textarea rows={5} value={s.intro} onChange={(v) => setS({ ...s, intro: v, intro_is_sample: false })} testid="admin-intro" /></div>
        <div className="mt-5"><In label="Invitation line" value={s.invitation} onChange={set("invitation")} testid="admin-invitation" /></div>
        <div className="mt-6">
          <span className="lou-label">Portrait</span>
          <div className="flex items-end gap-4">
            {s.portrait_media_id ? <Thumbs ids={[s.portrait_media_id]} onRemove={() => setS({ ...s, portrait_media_id: null })} />
              : <p className="text-sm text-lou-soft">Showing a sample placeholder.</p>}
            <Uploader label="Upload portrait" testid="admin-portrait-upload" onUploaded={([m]) => setS({ ...s, portrait_media_id: m.id })} />
          </div>
          <div className="mt-4 max-w-md"><In label="Portrait caption (also used as its description)" value={s.portrait_caption} onChange={set("portrait_caption")} testid="admin-portrait-caption" /></div>
        </div>
      </Panel>
      <Panel title="Featured artwork on the homepage" testid="admin-featured-art">
        <p className="text-sm text-lou-soft mb-4">Photographs and memories are featured from their own tabs. Save the homepage after changing these.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(arts || []).filter((a) => a.status === "published").map((a) => (
            <div key={a.id} className="flex items-center gap-3 border border-lou-line p-2">
              <img src={imgOf(a, true)} alt="" className="h-12 w-12 object-contain bg-lou-ivory" />
              <Check label={<>{a.title || "Untitled"}<SampleBadge show={a.is_sample} /></>} checked={s.featured_artwork_ids.includes(a.id)}
                onChange={(on) => toggleArt(a.id, on)} testid={`admin-feature-art-${a.id}`} />
            </div>
          ))}
        </div>
      </Panel>
    </>
  );
}

const SECTIONS = [["creativity", "Her creativity"], ["family", "Family life"], ["travels", "Travels"], ["traditions", "Traditions"], ["recipes", "Recipes"], ["expressions", "Favourite expressions"]];
const blankEvent = { title: "", date_text: "", sort_year: "", place: "", description: "", media_ids: [], artwork_ids: [], story_ids: [], status: "published" };

function TimelineEditor() {
  const [items, load] = useAdmin("/c/timeline");
  const [edit, setEdit] = useState(null);
  const [arts] = useAdmin("/c/artworks");
  const submit = async () => {
    const body = { ...edit, sort_year: edit.sort_year ? Number(edit.sort_year) : null };
    await save(() => (edit.id ? adminApi.put(`/c/timeline/${edit.id}`, body) : adminApi.post("/c/timeline", body)));
    setEdit(null); load();
  };
  const del = async (id) => { if (window.confirm("Delete this timeline entry?")) { await save(() => adminApi.delete(`/c/timeline/${id}`), "Deleted"); load(); } };
  return (
    <Panel title="Timeline" testid="admin-timeline"
      actions={<button className="lou-btn-ghost text-sm" onClick={() => setEdit({ ...blankEvent })} data-testid="admin-timeline-add">Add entry</button>}>
      {edit && (
        <div className="border border-lou-clay/40 p-4 mb-6 space-y-4 bg-lou-paper" data-testid="admin-timeline-form">
          <div className="grid md:grid-cols-3 gap-4">
            <In label="Title" value={edit.title} onChange={(v) => setEdit({ ...edit, title: v })} testid="tl-title" />
            <In label="Date as shown (approximate is fine)" placeholder="e.g. c. 1970s" value={edit.date_text} onChange={(v) => setEdit({ ...edit, date_text: v })} testid="tl-date" />
            <In label="Year for ordering (optional)" type="number" value={edit.sort_year ?? ""} onChange={(v) => setEdit({ ...edit, sort_year: v })} testid="tl-year" />
          </div>
          <In label="Place" value={edit.place} onChange={(v) => setEdit({ ...edit, place: v })} testid="tl-place" />
          <In label="Description" textarea value={edit.description} onChange={(v) => setEdit({ ...edit, description: v })} testid="tl-desc" />
          <div className="flex flex-wrap items-end gap-4">
            <Thumbs ids={edit.media_ids} onRemove={(id) => setEdit({ ...edit, media_ids: edit.media_ids.filter((x) => x !== id) })} />
            <Uploader multiple label="Add photographs" onUploaded={(ms) => setEdit({ ...edit, media_ids: [...edit.media_ids, ...ms.map((m) => m.id)] })} />
          </div>
          <label className="block"><span className="lou-label">Linked artwork</span>
            <select multiple className="lou-input text-sm h-28" value={edit.artwork_ids} onChange={(e) => setEdit({ ...edit, artwork_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}>
              {(arts || []).map((a) => <option key={a.id} value={a.id}>{a.title || "Untitled"}</option>)}
            </select></label>
          <Check label="Published" checked={edit.status === "published"} onChange={(on) => setEdit({ ...edit, status: on ? "published" : "draft" })} />
          <div className="flex gap-3"><button className="lou-btn !py-2 text-sm" onClick={submit} data-testid="tl-save">Save entry</button>
            <button className="lou-btn-ghost text-sm" onClick={() => setEdit(null)}>Cancel</button></div>
        </div>
      )}
      <ul className="divide-y divide-lou-line">
        {(items || []).map((t) => (
          <li key={t.id} className="py-3 flex items-center justify-between gap-4 text-sm" data-testid={`admin-tl-${t.id}`}>
            <span><span className="text-lou-soft">{t.date_text || "Undated"}</span> — {t.title}<SampleBadge show={t.is_sample} />{t.status !== "published" && <em className="text-lou-soft ml-2">draft</em>}</span>
            <span className="flex gap-3"><button className="underline" onClick={() => setEdit({ ...blankEvent, ...t })}>Edit</button>
              <button className="underline text-red-700" onClick={() => del(t.id)}>Delete</button></span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

const lines = (v) => v.split("\n").map((x) => x.trim()).filter(Boolean);
const awardText = (a) => (a || []).map((w) => `${w.year} | ${w.text}`).join("\n");
const parseAwards = (v) => lines(v).map((l) => { const [year, ...t] = l.split("|"); return { year: year.trim(), text: t.join("|").trim() }; });

function CareerEditor({ b, setB, submit }) {
  const c = b.career || {};
  const [raw, setRaw] = useState({});
  const val = (k, fmt) => raw[k] ?? fmt(c[k]);
  const set = (k, parse) => (v) => { setRaw({ ...raw, [k]: v }); setB({ ...b, career: { ...c, [k]: parse(v) } }); };
  const list = (x) => (x || []).join("\n");
  return (
    <Panel title="Awards and exhibitions" testid="admin-career"
      actions={<button className="lou-btn !py-2 text-sm" onClick={submit} data-testid="admin-career-save">Save</button>}>
      <p className="text-sm text-lou-soft mb-4">One per line. Awards: <em>year | award</em>.</p>
      <div className="grid md:grid-cols-2 gap-5">
        <In label="Awards" textarea rows={8} value={val("awards", awardText)} onChange={set("awards", parseAwards)} testid="admin-career-awards" />
        <In label="Exhibitions" textarea rows={8} value={val("exhibitions", list)} onChange={set("exhibitions", lines)} testid="admin-career-exhibitions" />
        <In label="Education" textarea rows={5} value={val("education", list)} onChange={set("education", lines)} testid="admin-career-education" />
        <In label="Affiliations" textarea rows={5} value={val("affiliations", list)} onChange={set("affiliations", lines)} testid="admin-career-affiliations" />
        <In label="Collections" value={c.collections} onChange={(v) => setB({ ...b, career: { ...c, collections: v } })} testid="admin-career-collections" />
      </div>
    </Panel>
  );
}

export function LifeTab() {
  const [b, load, setB] = useAdmin("/biography");
  if (!b) return null;
  const sec = b.sections || {};
  const submit = async () => { await save(() => adminApi.put("/biography", b)); load(); };
  return (
    <>
      <Panel title="Biography — written by the family" testid="admin-bio"
        actions={<button className="lou-btn !py-2 text-sm" onClick={submit} data-testid="admin-bio-save">Save biography</button>}>
        {b.is_sample && <p className="text-sm text-lou-ochre mb-4">This is sample text. Saving replaces it as family-authored content.</p>}
        <In label="Short introduction" textarea rows={3} value={b.short_intro} onChange={(v) => setB({ ...b, short_intro: v })} testid="admin-bio-short" />
        <div className="mt-5"><In label="Full life story" textarea rows={12} value={b.full_story} onChange={(v) => setB({ ...b, full_story: v })} testid="admin-bio-full" /></div>
        <p className="text-sm text-lou-soft mt-6 mb-3">Optional sections appear on the site only when they have text.</p>
        <div className="grid md:grid-cols-2 gap-5">
          {SECTIONS.map(([k, t]) => <In key={k} label={t} textarea rows={4} value={sec[k]} onChange={(v) => setB({ ...b, sections: { ...sec, [k]: v } })} testid={`admin-bio-${k}`} />)}
        </div>
      </Panel>
      <CareerEditor b={b} setB={setB} submit={submit} />
      <TimelineEditor />
    </>
  );
}
